import { toSpaceKey } from '@toolproof-core/visualization/_pure/contract-helpers';
import type {
    CollectionKey,
    ConstellationKey,
    ItemKey,
} from '@toolproof-core/visualization/_contract/key';

import type { PrimitiveWheelRole, PrimitiveWheelState } from '@/adapters/primitive/_contract/config';

type BandRef = {
    wheelRole: PrimitiveWheelRole;
    variant?: PrimitiveWheelState['variant'];
    power: number;
};

/** Keeps a band in transit distinct from the record left at its wheel. */
function toBandScope(args: BandRef): string {
    return `${args.wheelRole}${args.variant === undefined ? '' : `:${args.variant}`}:power:${args.power}`;
}

export function toPrimitivePowerCollectionKey(args: BandRef): CollectionKey<'entity'> {
    return `collection:entity:primitive:${toBandScope(args)}` as CollectionKey<'entity'>;
}

export function toPrimitivePowerConstellationKey(args: BandRef): ConstellationKey<'entity'> {
    return `constellation:entity:primitive:${toBandScope(args)}` as ConstellationKey<'entity'>;
}

export function toNaturalDigitItemKey(args: BandRef & { digit: number }): ItemKey<'entity'> {
    return `item:entity:primitive:${toBandScope(args)}:digit:${args.digit}` as ItemKey<'entity'>;
}

export function toBooleanDiscItemKey(args: BandRef): ItemKey<'entity'> {
    return `item:entity:primitive:${toBandScope(args)}:boolean-disc` as ItemKey<'entity'>;
}

export function toBooleanRodCollectionKey(): CollectionKey<'connector'> {
    return 'collection:connector:primitive:boolean-rod' as CollectionKey<'connector'>;
}

export function toBooleanRodConstellationKey(): ConstellationKey<'connector'> {
    return 'constellation:connector:primitive:boolean-rod' as ConstellationKey<'connector'>;
}

export function toBooleanRodItemKey(segmentKey: string): ItemKey<'connector'> {
    return `item:connector:primitive:boolean-rod:${segmentKey}` as ItemKey<'connector'>;
}

export function toBooleanChannelSegmentKey(pathKey: string, section: 'output' | 'output-near' | 'output-far'): string {
    return `${pathKey}:${section}:channel`;
}

export function toBooleanOperandSegmentKey(pathKey: string, role: 'boolean-input-one' | 'boolean-input-two'): string {
    return `${pathKey}:${role === 'boolean-input-one' ? 'input-one' : 'input-two'}`;
}

export function toBooleanTravellerSegmentKey(pathKey: string, role: 'boolean-input-one' | 'boolean-input-two'): string {
    return `traveller:${pathKey}:${role}`;
}

export function toBooleanProfileCollectionKey(segmentKey: string): CollectionKey<'entity'> {
    return `collection:entity:primitive:boolean-profile:${segmentKey}` as CollectionKey<'entity'>;
}

export function toBooleanProfileConstellationKey(segmentKey: string): ConstellationKey<'entity'> {
    return `constellation:entity:primitive:boolean-profile:${segmentKey}` as ConstellationKey<'entity'>;
}

export function toBooleanProfileItemKey(segmentKey: string): ItemKey<'entity'> {
    return `item:entity:primitive:boolean-profile:${segmentKey}` as ItemKey<'entity'>;
}

export function toBooleanRodAnchorCollectionKey(segmentKey: string, endpoint: 'from' | 'to'): CollectionKey<'entity'> {
    return `collection:entity:primitive:boolean-rod:${segmentKey}:${endpoint}` as CollectionKey<'entity'>;
}

export function toBooleanRodAnchorConstellationKey(segmentKey: string, endpoint: 'from' | 'to'): ConstellationKey<'entity'> {
    return `constellation:entity:primitive:boolean-rod:${segmentKey}:${endpoint}` as ConstellationKey<'entity'>;
}

export function toBooleanRodAnchorItemKey(segmentKey: string, endpoint: 'from' | 'to'): ItemKey<'entity'> {
    return `item:entity:primitive:boolean-rod:${segmentKey}:${endpoint}` as ItemKey<'entity'>;
}

/** Stable wheels own one hub; a transient wheel's hub is keyed by its innermost band, sharing that band's lifetime. */
type HubRef = Omit<BandRef, 'power'> & { power?: number };

function toHubScope(args: HubRef): string {
    return `${args.wheelRole}${args.variant === undefined ? '' : `:${args.variant}`}${args.power === undefined ? '' : `:${args.power}`}:hub`;
}

export function toPrimitiveHubCollectionKey(args: HubRef): CollectionKey<'entity'> {
    return `collection:entity:primitive:${toHubScope(args)}` as CollectionKey<'entity'>;
}

export function toPrimitiveHubConstellationKey(args: HubRef): ConstellationKey<'entity'> {
    return `constellation:entity:primitive:${toHubScope(args)}` as ConstellationKey<'entity'>;
}

export function toPrimitiveHubItemKey(args: HubRef): ItemKey<'entity'> {
    return `item:entity:primitive:${toHubScope(args)}` as ItemKey<'entity'>;
}

export function toPrimitiveWheelConnectorAnchorCollectionKey(args: {
    wheelRole: PrimitiveWheelRole;
    side: 'min' | 'max';
}): CollectionKey<'entity'> {
    return `collection:entity:primitive:${args.wheelRole}:workflow-connector-anchor:${args.side}` as CollectionKey<'entity'>;
}

export function toPrimitiveWheelConnectorAnchorConstellationKey(args: {
    wheelRole: PrimitiveWheelRole;
    side: 'min' | 'max';
}): ConstellationKey<'entity'> {
    return `constellation:entity:primitive:${args.wheelRole}:workflow-connector-anchor:${args.side}` as ConstellationKey<'entity'>;
}

export function toPrimitiveWheelConnectorAnchorItemKey(args: {
    wheelRole: PrimitiveWheelRole;
    side: 'min' | 'max';
}): ItemKey<'entity'> {
    return `item:entity:primitive:${args.wheelRole}:workflow-connector-anchor:${args.side}` as ItemKey<'entity'>;
}

export function toNaturalAxialTieCollectionKey(): CollectionKey<'connector'> {
    return 'collection:connector:primitive:axial-ties' as CollectionKey<'connector'>;
}

export function toPrimitiveAxisRodCollectionKey(): CollectionKey<'connector'> {
    return 'collection:connector:primitive:axis-rod' as CollectionKey<'connector'>;
}

export function toPrimitiveAxisRodItemKey(): ItemKey<'connector'> {
    return 'item:connector:primitive:axis-rod' as ItemKey<'connector'>;
}

type CanonicalZeroRef = {
    wheelRole: PrimitiveWheelRole;
    power: number;
};

export function toNaturalBandKeyAnchorKey(args: CanonicalZeroRef & { point: string }): ItemKey<'entity'> {
    return `item:entity:primitive:${args.wheelRole}:band-key:power:${args.power}:${args.point}` as ItemKey<'entity'>;
}

export function toNaturalBandKeyGuideKey(args: CanonicalZeroRef & { part: string }): ItemKey<'connector'> {
    return `item:connector:primitive:${args.wheelRole}:band-key:power:${args.power}:${args.part}` as ItemKey<'connector'>;
}

export function toNaturalBandKeyGuideCollectionKey(args: CanonicalZeroRef): CollectionKey<'connector'> {
    return `collection:connector:primitive:${args.wheelRole}:band-key:power:${args.power}` as CollectionKey<'connector'>;
}

export function toNaturalBandKeyGuideConstellationKey(args: CanonicalZeroRef): ConstellationKey<'connector'> {
    return `constellation:connector:primitive:${args.wheelRole}:band-key:power:${args.power}` as ConstellationKey<'connector'>;
}

export function toNaturalCanonicalZeroCollectionKey(args: CanonicalZeroRef): CollectionKey<'entity'> {
    return `collection:entity:primitive:${args.wheelRole}:canonical-zero:power:${args.power}` as CollectionKey<'entity'>;
}

export function toNaturalCanonicalZeroConstellationKey(args: CanonicalZeroRef): ConstellationKey<'entity'> {
    return `constellation:entity:primitive:${args.wheelRole}:canonical-zero:power:${args.power}` as ConstellationKey<'entity'>;
}

export function toNaturalCanonicalZeroItemKey(args: CanonicalZeroRef): ItemKey<'entity'> {
    return `item:entity:primitive:${args.wheelRole}:canonical-zero:power:${args.power}` as ItemKey<'entity'>;
}

type SettlementWallRef = {
    unitKey: string;
    /** A settling power, or `envelope` for a single wall around all of them. */
    part: number | 'envelope';
};

export function toSettlementWallCollectionKey(args: SettlementWallRef): CollectionKey<'entity'> {
    return `collection:entity:primitive:settlement-wall:${args.unitKey}:${args.part}` as CollectionKey<'entity'>;
}

export function toSettlementWallConstellationKey(args: SettlementWallRef): ConstellationKey<'entity'> {
    return `constellation:entity:primitive:settlement-wall:${args.unitKey}:${args.part}` as ConstellationKey<'entity'>;
}

export function toSettlementWallItemKey(args: SettlementWallRef): ItemKey<'entity'> {
    return `item:entity:primitive:settlement-wall:${args.unitKey}:${args.part}` as ItemKey<'entity'>;
}

export function toPrimitiveAxisRodConstellationKey(): ConstellationKey<'connector'> {
    return 'constellation:connector:primitive:axis-rod' as ConstellationKey<'connector'>;
}

export function toPrimitiveAxisRodMarkerCollectionKey(args: { endpoint: 'front' | 'back' }): CollectionKey<'entity'> {
    return `collection:entity:primitive:axis-rod-marker:${args.endpoint}` as CollectionKey<'entity'>;
}

export function toPrimitiveAxisRodMarkerConstellationKey(args: { endpoint: 'front' | 'back' }): ConstellationKey<'entity'> {
    return `constellation:entity:primitive:axis-rod-marker:${args.endpoint}` as ConstellationKey<'entity'>;
}

export function toPrimitiveAxisRodMarkerItemKey(args: { endpoint: 'front' | 'back' }): ItemKey<'entity'> {
    return `item:entity:primitive:axis-rod-marker:${args.endpoint}` as ItemKey<'entity'>;
}

type WheelTitleRef = { wheelRole: PrimitiveWheelRole; helperKey?: string };

function toWheelTitleScope(args: WheelTitleRef): string {
    return args.helperKey === undefined ? args.wheelRole : `helper:${args.helperKey}`;
}

export function toPrimitiveWheelTitleCollectionKey(args: WheelTitleRef): CollectionKey<'entity'> {
    return `collection:entity:primitive:${toWheelTitleScope(args)}:title` as CollectionKey<'entity'>;
}

export function toPrimitiveWheelTitleConstellationKey(args: WheelTitleRef): ConstellationKey<'entity'> {
    return `constellation:entity:primitive:${toWheelTitleScope(args)}:title` as ConstellationKey<'entity'>;
}

export function toPrimitiveWheelTitleItemKey(args: WheelTitleRef): ItemKey<'entity'> {
    return `item:entity:primitive:${toWheelTitleScope(args)}:title` as ItemKey<'entity'>;
}

export function toNaturalRadialLabelCollectionKey(args: { wheelRole: PrimitiveWheelRole; power: number }): CollectionKey<'entity'> {
    return `collection:entity:primitive:${args.wheelRole}:radial-label:power:${args.power}` as CollectionKey<'entity'>;
}

export function toNaturalRadialLabelConstellationKey(args: { wheelRole: PrimitiveWheelRole; power: number }): ConstellationKey<'entity'> {
    return `constellation:entity:primitive:${args.wheelRole}:radial-label:power:${args.power}` as ConstellationKey<'entity'>;
}

export function toNaturalRadialLabelItemKey(args: { wheelRole: PrimitiveWheelRole; power: number }): ItemKey<'entity'> {
    return `item:entity:primitive:${args.wheelRole}:radial-label:power:${args.power}` as ItemKey<'entity'>;
}

export function toNaturalAngularLabelCollectionKey(args: { wheelRole: PrimitiveWheelRole; digit: number }): CollectionKey<'entity'> {
    return `collection:entity:primitive:${args.wheelRole}:angular-label:digit:${args.digit}` as CollectionKey<'entity'>;
}

export function toNaturalAngularLabelConstellationKey(args: { wheelRole: PrimitiveWheelRole; digit: number }): ConstellationKey<'entity'> {
    return `constellation:entity:primitive:${args.wheelRole}:angular-label:digit:${args.digit}` as ConstellationKey<'entity'>;
}

export function toNaturalAngularLabelItemKey(args: { wheelRole: PrimitiveWheelRole; digit: number }): ItemKey<'entity'> {
    return `item:entity:primitive:${args.wheelRole}:angular-label:digit:${args.digit}` as ItemKey<'entity'>;
}

export function toNaturalAxialTieConstellationKey(): ConstellationKey<'connector'> {
    return 'constellation:connector:primitive:axial-ties' as ConstellationKey<'connector'>;
}

export function toNaturalAxialTieItemKey(args: {
    nearRole: PrimitiveWheelRole;
    farRole: PrimitiveWheelRole;
    power: number;
    digit: number;
}): ItemKey<'connector'> {
    return `item:connector:primitive:tie:${args.nearRole}:${args.farRole}:power:${args.power}:digit:${args.digit}` as ItemKey<'connector'>;
}

export type ComparisonRaySide = 'source' | 'target';
export function toNaturalComparisonOrbitCollectionKey(): CollectionKey<'entity'> {
    return 'collection:entity:primitive:comparison-orbit' as CollectionKey<'entity'>;
}

export function toNaturalComparisonOrbitConstellationKey(): ConstellationKey<'entity'> {
    return 'constellation:entity:primitive:comparison-orbit' as ConstellationKey<'entity'>;
}

export function toNaturalComparisonOrbitItemKey(): ItemKey<'entity'> {
    return 'item:entity:primitive:comparison-orbit' as ItemKey<'entity'>;
}

export type ComparisonCellBoundary = 'start' | 'end';
export type ComparisonCellBoundaryEdge = 'inner' | 'outer';

export function toNaturalComparisonAnchorCollectionKey(args: { side: ComparisonRaySide }): CollectionKey<'entity'> {
    return `collection:entity:primitive:comparison-anchor:${args.side}` as CollectionKey<'entity'>;
}

export function toNaturalComparisonAnchorConstellationKey(args: { side: ComparisonRaySide }): ConstellationKey<'entity'> {
    return `constellation:entity:primitive:comparison-anchor:${args.side}` as ConstellationKey<'entity'>;
}

export function toNaturalComparisonAnchorItemKey(args: { side: ComparisonRaySide }): ItemKey<'entity'> {
    return `item:entity:primitive:comparison-anchor:${args.side}` as ItemKey<'entity'>;
}

export function toNaturalComparisonRayCollectionKey(): CollectionKey<'connector'> {
    return 'collection:connector:primitive:comparison-rays' as CollectionKey<'connector'>;
}

export function toNaturalComparisonRayConstellationKey(): ConstellationKey<'connector'> {
    return 'constellation:connector:primitive:comparison-rays' as ConstellationKey<'connector'>;
}

export function toNaturalComparisonRayItemKey(args: { side: ComparisonRaySide }): ItemKey<'connector'> {
    return `item:connector:primitive:comparison-ray:${args.side}` as ItemKey<'connector'>;
}

type ComparisonCellBoundaryRef = {
    side: ComparisonRaySide;
    boundary: ComparisonCellBoundary;
    edge: ComparisonCellBoundaryEdge;
};

export function toNaturalComparisonCellBoundaryAnchorCollectionKey(
    args: ComparisonCellBoundaryRef,
): CollectionKey<'entity'> {
    return `collection:entity:primitive:comparison-cell-boundary:${args.side}:${args.boundary}:${args.edge}` as CollectionKey<'entity'>;
}

export function toNaturalComparisonCellBoundaryAnchorConstellationKey(
    args: ComparisonCellBoundaryRef,
): ConstellationKey<'entity'> {
    return `constellation:entity:primitive:comparison-cell-boundary:${args.side}:${args.boundary}:${args.edge}` as ConstellationKey<'entity'>;
}

export function toNaturalComparisonCellBoundaryAnchorItemKey(
    args: ComparisonCellBoundaryRef,
): ItemKey<'entity'> {
    return `item:entity:primitive:comparison-cell-boundary:${args.side}:${args.boundary}:${args.edge}` as ItemKey<'entity'>;
}

export function toNaturalComparisonCellBoundaryItemKey(args: {
    side: ComparisonRaySide;
    boundary: ComparisonCellBoundary;
}): ItemKey<'connector'> {
    return `item:connector:primitive:comparison-cell-boundary:${args.side}:${args.boundary}` as ItemKey<'connector'>;
}